import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import OpportunityInfoTab from "./OpportunityInfoTab";
import DocumentsTab from "../opportunity-documents/DocumentsTab.jsx";
import OpportunityCoversionTab from "./OpportunityConversionTab";
import ConversionTab from "./ConversionTab";
import HunterDropdown from "./HunterDropdown";
import { apiRequest } from "@/network/apiClient";

const BASE_URL = import.meta.env.VITE_BASE_URL;

// ---------- Constants ----------
const FUNNEL_TYPES = {
  RE_KYC: "Re-KYC",
  REFERRAL: "Referral",
  FINANCING: "Financing",
  INSURANCE: "Insurance",
};

const TAB_CONFIG = {
  [FUNNEL_TYPES.RE_KYC]: ["Qualification", "Documents"],
  [FUNNEL_TYPES.REFERRAL]: ["Qualification", "Info Gathering"],
  [FUNNEL_TYPES.FINANCING]: ["Qualification", "Info Gathering", "Conversion"], // no Docs
  [FUNNEL_TYPES.INSURANCE]: ["Qualification", "Info Gathering", "Documents", "Conversion"],
  default: [],
};

// ---------- Reusable UI helpers ----------
const CopyButton = ({
  text,
  className = "ml-2 bg-white text-blue-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500",
}) => {
  if (!text) return null;

  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
      }}
      className={className}
      type="button"
    >
      <img src="/icons/content_copy.svg" className="size-6" alt="Copy" />
    </button>
  );
};

const FieldRow = ({
  label,
  value,
  isEditing,
  onChange,
  name,
  disabled = false,
  showCopy = true,
}) => (
  <div className="flex flex-col md:flex-row md:items-center mt-3 md:mt-5">
    <div className="border-b border-gray-200 pb-2 w-full flex flex-col md:flex-row gap-1 md:gap-0">
      <span className="w-full md:w-1/5 font-bold text-[#475569]">{label}</span>
      {isEditing ? (
        <input
          type="text"
          name={name}
          disabled={disabled}
          value={value || ""}
          onChange={onChange}
          className="w-full md:flex-1 text-[#475569]"
        />
      ) : (
        <>
          <div className="flex-1 flex items-center">
            <span className="flex-1 text-[#475569]">{value || "N/A"}</span>
          </div>
          {showCopy && <CopyButton text={value} />}
        </>
      )}
    </div>
  </div>
);

// ---------- Main Component ----------
const OpportunityDetails = ({ oppData }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [referralData, setReferralData] = useState({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [initialPayoutValue, setInitialPayoutValue] = useState(null);
  const [showSaveButtons, setShowSaveButtons] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const isReferralFunnel = oppData?.funnel === FUNNEL_TYPES.REFERRAL;
  const isReKycFunnel = oppData?.funnel === FUNNEL_TYPES.RE_KYC;
  const isFinancingFunnel = oppData?.funnel === FUNNEL_TYPES.FINANCING;
  const isInsuranceFunnel = oppData?.funnel === FUNNEL_TYPES.INSURANCE;

  // Tabs based on funnel type
  const tabs = useMemo(() => {
    return TAB_CONFIG[oppData?.funnel] || TAB_CONFIG.default;
  }, [oppData?.funnel]);

  // Handlers
  const handleEditClick = useCallback(() => {
    setIsEditing((prev) => !prev);
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      // mutate oppData (same as old/new behaviour)
      if (oppData) {
        oppData[name] = value;
      }
    },
    [oppData]
  );

  const handlePayoutCheckboxChange = useCallback(
    (e) => {
      const isQualified = e.target.checked ? 1 : 0;
      setReferralData((prev) => ({
        ...prev,
        payout: isQualified,
      }));

      setShowSaveButtons(isQualified !== initialPayoutValue);
    },
    [initialPayoutValue]
  );

  const handleCancel = useCallback(() => {
    setReferralData((prev) => ({
      ...prev,
      payout: initialPayoutValue,
    }));
    setShowSaveButtons(false);
  }, [initialPayoutValue]);

  const handleSubmit = useCallback(async () => {
    if (!oppData?.referred_lead_id) {
      toast.error("Missing referred lead ID");
      return;
    }

    try {
      const result = await apiRequest(`${BASE_URL}/update_referral_payout`, 'POST', {
          lead_id: oppData.referred_lead_id,
          payout: referralData.payout,
        })

      if (!result) {
        throw new Error("Failed to update payout qualification");
      }

      setInitialPayoutValue(referralData.payout);
      setShowSaveButtons(false);
      setShowConfirmationModal(false);
      toast.success("Payout qualification updated successfully");
    } catch (error) {
      console.error("Error updating payout qualification:", error);
      toast.error("Failed to update payout qualification");
    }
  }, [oppData?.referred_lead_id, referralData.payout]);

  // Referral data fetch (old logic, adjusted to VITE)
  useEffect(() => {
    if (!oppData?.lead_id || !isReferralFunnel || !oppData?.referred_lead_id) {
      return;
    }

    const fetchReferralData = async () => {
      try {
        const result = await apiRequest(`${BASE_URL}/referral_details?lead_id=${oppData.referred_lead_id}`)

        if (!result) {
          throw new Error("Failed to fetch referral data");
        }

        setReferralData(result);
        setInitialPayoutValue(result.payout);
      } catch (error) {
        console.error("Error fetching referral details:", error);
        toast.error("Failed to load referral details");
      }
    };

    fetchReferralData();
  }, [oppData?.lead_id, oppData?.referred_lead_id, isReferralFunnel]);

  // Render sections
  const renderReferralDetails = () => {
    if (!isReferralFunnel) return null;

    return (
      <div>
        <br />
        <div className="flex justify-between items-start mt-2">
          <div className="flex items-center">
            <p className="font-bold text-2xl text-[#1E293B]">Referred Details</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center mt-3 md:mt-5">
          <span className="border-b border-gray-200 pb-2 w-full flex flex-col md:flex-row gap-1 md:gap-0">
            <span className="w-full md:w-1/5 font-bold text-[#475569]">
              Referred Company Name
            </span>
            <span className="flex-1 text-[#475569]">
              {referralData.company_name || "N/A"}
            </span>
          </span>
        </div>

        <div className="flex flex-col md:flex-row md:items-center mt-3 md:mt-5">
          <span className="border-b border-gray-200 pb-2 w-full flex flex-col md:flex-row gap-1 md:gap-0">
            <span className="w-full md:w-1/5 font-bold text-[#475569]">Lead Source</span>
            <span className="flex-1 text-[#475569]">
              {referralData.lead_source || "N/A"}
            </span>
          </span>
        </div>

        <div className="flex items-center mt-5">
          <span className="pb-2 w-full flex">
            <span className="font-bold w-1/5 text-[#475569]">
              Qualified for payout
            </span>
            <input
              type="checkbox"
              checked={referralData.payout === 1}
              onChange={handlePayoutCheckboxChange}
              className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </span>
        </div>

        {/* Save/Cancel buttons (new code style) */}
        {showSaveButtons && (
          <div className="flex justify-end space-x-4 mt-6">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-3xl text-gray-700 hover:bg-gray-100"
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={() => setShowConfirmationModal(true)}
              className="px-4 py-2 bg-yellow-300 text-black rounded-3xl hover:bg-yellow-400"
              type="button"
            >
              Save Changes
            </button>
          </div>
        )}

        {/* Confirmation Modal (new code style, old logic handler) */}
        {showConfirmationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center text-center">
            <div className="bg-white p-6 rounded-lg w-1/4">
              <div className="flex justify-center mb-2 pt-6">
                <img src="/icons/edit.svg" className="size-11" alt="Edit" />
              </div>
              <h1 className="text-2xl font-bold mb-3">
                Confirm Payout Qualification Update
              </h1>
              <p>Are you sure you want to update the payout qualification status?</p>
              <div className="mt-6 mb-6">
                <button
                  className="text-black underline hover:text-gray-600 focus:outline-none"
                  onClick={() => setShowConfirmationModal(false)}
                  type="button"
                >
                  <b>No, Don&apos;t Update</b>
                </button>
              </div>
              <button
                className="bg-yellow-300 text-black py-4 w-full rounded-3xl hover:bg-yellow-400"
                onClick={handleSubmit}
                type="button"
              >
                <b>Yes, Update</b>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderQualificationTab = () => (
    <div className="w-full bg-white p-7 rounded-3xl">
      <div className="flex justify-between items-start">
        <div className="flex items-center">
          <p className="font-bold text-2xl text-[#1E293B]">Qualification</p>
        </div>
      </div>

      {/* MM Account ID (old FieldRow pattern) */}
      <FieldRow
        label="MM Account ID"
        value={oppData?.mm_acc_id}
        isEditing={isEditing}
        onChange={handleChange}
        name="mm_acc_id"
        disabled={true}
      />

      {/* Opportunity Remark: hidden for Referral (old logic) */}
      {!isReferralFunnel && (
        <FieldRow
          label="Opportunity Remark"
          value={oppData?.opp_name}
          isEditing={isEditing}
          onChange={handleChange}
          name="opp_name"
        />
      )}

      {/* Account Name */}
      <FieldRow
        label="Account Name"
        value={oppData?.name}
        isEditing={isEditing}
        onChange={handleChange}
        name="name"
      />

      {/* Ref ID for Insurance (old code logic, correct copy value) */}
      {isInsuranceFunnel && (
        <div className="flex flex-col md:flex-row md:items-center mt-3 md:mt-5">
          <span className="border-b border-gray-200 pb-2 w-full flex flex-col md:flex-row gap-1 md:gap-0">
            <span className="w-full md:w-1/5 font-bold text-[#475569]">Ref ID</span>
            <span className="flex-1 text-[#475569]">
              {oppData?.ref_id || "N/A"}
            </span>
            <CopyButton text={oppData?.ref_id} />
          </span>
        </div>
      )}

      {/* Referral extra block (new UI style + old logic) */}
      {renderReferralDetails()}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderQualificationTab();

      case 1:
        // Re-KYC → Documents; others → Info tab
        if (isReKycFunnel) {
          return (
            <DocumentsTab leadID={oppData.lead_id} funnel={oppData.funnel} />
          );
        }
        return (
          <OpportunityInfoTab
            funnel={oppData.funnel}
            oppID={oppData.sub_id}
            oppData={oppData}
          />
        );

      case 2:
        // Financing: Conversion (OpportunityCoversionTab), no Documents
        if (isFinancingFunnel) {
          return (
            <OpportunityCoversionTab
              oppID={oppData.sub_id}
              funnel={oppData.funnel}
              opprefID={oppData.ref_id}
              mm_acc_id={oppData.mm_acc_id}
            />
          );
        }

        // Referral: Documents for referred_lead_id
        if (isReferralFunnel) {
          return (
            <DocumentsTab
              leadID={oppData.referred_lead_id}
              funnel={oppData.funnel}
            />
          );
        }

        // Default / Insurance / others: Documents for normal lead
        return (
          <DocumentsTab leadID={oppData.lead_id} funnel={oppData.funnel} opprefID={oppData.ref_id} />
        );

      case 3:
        // Only funnels that actually have 4 tabs will hit here
        if (isReferralFunnel) {
          // Referral uses ConversionTab
          return <ConversionTab oppData={oppData} />;
        }

        // Others (e.g. Insurance, default) use OpportunityCoversionTab
        return (
          <OpportunityCoversionTab
            oppID={oppData.sub_id}
            funnel={oppData.funnel}
            opprefID={oppData.ref_id}
            mm_acc_id={oppData.mm_acc_id}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full flex justify-between items-center mx-2 md:ml-2 md:mr-0">
      <div className="p-3 md:p-4 rounded-2xl md:rounded-3xl w-full mr-0 md:mr-2">
        <div className="flex space-x-4 md:space-x-6 ml-2 md:ml-3 overflow-x-auto scrollbar-hide">
          {tabs.map((tab, index) => (
            <button
              key={`${tab}-${index}`}
              onClick={() => setActiveTab(index)}
              className={`relative text-sm md:text-md font-medium pb-1 transition-all mb-3 whitespace-nowrap flex-shrink-0 ${
                activeTab === index
                  ? "text-black border-b-4 px-2 border-yellow-300"
                  : "text-gray-500"
              }`}
              type="button"
            >
              {tab}
            </button>
          ))}
        </div>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default OpportunityDetails;